import { supabase } from './supabase'
import type { Fornecedor, TipoPessoa } from '../types/database'

export type FornecedorInput = {
  razao_social: string
  nome_fantasia: string | null
  tipo_pessoa: TipoPessoa
  cpf_cnpj: string | null
  cep: string | null
  logradouro: string | null
  numero: string | null
  complemento: string | null
  bairro: string | null
  cidade: string | null
  uf: string | null
  responsavel_nome: string | null
  telefone: string | null
  celular: string | null
  email: string | null
  ativo: boolean
}

export const FORNECEDOR_TEMPLATE_HEADERS = [
  'razao_social',
  'nome_fantasia',
  'tipo_pessoa',
  'cpf_cnpj',
  'cep',
  'logradouro',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  'uf',
  'responsavel_nome',
  'telefone',
  'celular',
  'email',
] as const

export const FORNECEDOR_TEMPLATE_ROW: Record<(typeof FORNECEDOR_TEMPLATE_HEADERS)[number], string> = {
  razao_social: 'Empresa Exemplo LTDA',
  nome_fantasia: 'Exemplo Alimentos',
  tipo_pessoa: 'PJ',
  cpf_cnpj: '00.000.000/0001-00',
  cep: '00000-000',
  logradouro: 'Rua Exemplo',
  numero: '100',
  complemento: 'Sala 1',
  bairro: 'Centro',
  cidade: 'São Paulo',
  uf: 'SP',
  responsavel_nome: 'João Silva',
  telefone: '(11) 3333-4444',
  celular: '(11) 99999-8888',
  email: 'contato@exemplo.com.br',
}

export function onlyDigits(value: string) {
  return value.replace(/\D/g, '')
}

export function formatCpfCnpj(value: string, tipo: TipoPessoa) {
  const d = onlyDigits(value)
  if (tipo === 'PF') {
    if (d.length <= 11) {
      return d
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1-$2')
        .slice(0, 14)
    }
  } else if (d.length <= 14) {
    return d
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18)
  }
  return value
}

export function inferTipoPessoa(cpfCnpj: string): TipoPessoa {
  return onlyDigits(cpfCnpj).length <= 11 ? 'PF' : 'PJ'
}

export function validateFornecedorInput(input: FornecedorInput): string | null {
  if (!input.razao_social.trim()) return 'Razão social é obrigatória.'
  if (!['PF', 'PJ'].includes(input.tipo_pessoa)) return 'Tipo de pessoa inválido.'
  if (input.cpf_cnpj) {
    const len = onlyDigits(input.cpf_cnpj).length
    if (input.tipo_pessoa === 'PF' && len !== 11) return 'CPF deve ter 11 dígitos.'
    if (input.tipo_pessoa === 'PJ' && len !== 14) return 'CNPJ deve ter 14 dígitos.'
  }
  if (input.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email)) {
    return 'E-mail inválido.'
  }
  if (input.uf && input.uf.length !== 2) return 'UF deve ter 2 letras.'
  return null
}

export function normalizeFornecedorInput(raw: Partial<FornecedorInput>): FornecedorInput {
  const tipo = (raw.tipo_pessoa === 'PF' || raw.tipo_pessoa === 'PJ'
    ? raw.tipo_pessoa
    : inferTipoPessoa(raw.cpf_cnpj || '')) as TipoPessoa
  const cpfRaw = raw.cpf_cnpj?.trim() || ''
  return {
    razao_social: (raw.razao_social || '').trim(),
    nome_fantasia: raw.nome_fantasia?.trim() || null,
    tipo_pessoa: tipo,
    cpf_cnpj: cpfRaw ? formatCpfCnpj(cpfRaw, tipo) : null,
    cep: raw.cep?.trim() || null,
    logradouro: raw.logradouro?.trim() || null,
    numero: raw.numero?.trim() || null,
    complemento: raw.complemento?.trim() || null,
    bairro: raw.bairro?.trim() || null,
    cidade: raw.cidade?.trim() || null,
    uf: raw.uf?.trim().toUpperCase().slice(0, 2) || null,
    responsavel_nome: raw.responsavel_nome?.trim() || null,
    telefone: raw.telefone?.trim() || null,
    celular: raw.celular?.trim() || null,
    email: raw.email?.trim().toLowerCase() || null,
    ativo: raw.ativo !== false,
  }
}

export function fornecedorToInput(f: Fornecedor): FornecedorInput {
  return normalizeFornecedorInput({
    razao_social: f.razao_social,
    nome_fantasia: f.nome_fantasia || '',
    tipo_pessoa: f.tipo_pessoa,
    cpf_cnpj: f.cpf_cnpj || '',
    cep: f.cep || '',
    logradouro: f.logradouro || '',
    numero: f.numero || '',
    complemento: f.complemento || '',
    bairro: f.bairro || '',
    cidade: f.cidade || '',
    uf: f.uf || '',
    responsavel_nome: f.responsavel_nome || '',
    telefone: f.telefone || '',
    celular: f.celular || '',
    email: f.email || '',
    ativo: f.ativo,
  })
}

async function vincularContratosPorDocumento(fornecedorId: string, cpfCnpj: string | null) {
  if (!cpfCnpj) return
  const digits = onlyDigits(cpfCnpj)
  const { data: contratos } = await supabase
    .from('contratos')
    .select('id, cnpj, fornecedor_id')
    .is('fornecedor_id', null)

  if (!contratos?.length) return

  const ids = contratos
    .filter((c) => onlyDigits(c.cnpj || '') === digits)
    .map((c) => c.id)

  if (!ids.length) return

  await supabase.from('contratos').update({ fornecedor_id: fornecedorId }).in('id', ids)
}

async function vincularPrecosPorNome(fornecedorId: string, razaoSocial: string) {
  const { data: precos } = await supabase
    .from('precos')
    .select('id')
    .eq('fornecedor', razaoSocial)
    .is('fornecedor_id', null)

  if (!precos?.length) return
  await supabase
    .from('precos')
    .update({ fornecedor_id: fornecedorId })
    .in(
      'id',
      precos.map((p) => p.id),
    )
}

export async function salvarFornecedor(
  input: FornecedorInput,
  id?: string,
): Promise<{ data: Fornecedor | null; error: string | null }> {
  const normalized = normalizeFornecedorInput(input)
  const validation = validateFornecedorInput(normalized)
  if (validation) return { data: null, error: validation }

  const payload = {
    ...normalized,
    atualizado_em: new Date().toISOString(),
  }

  if (id) {
    const { data, error } = await supabase
      .from('fornecedores')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single()
    if (error) return { data: null, error: error.message }
    await vincularContratosPorDocumento(id, normalized.cpf_cnpj)
    return { data: data as Fornecedor, error: null }
  }

  const { data, error } = await supabase
    .from('fornecedores')
    .insert({ ...payload, criado_em: new Date().toISOString() })
    .select('*')
    .single()
  if (error) return { data: null, error: error.message }

  const fornecedor = data as Fornecedor
  await vincularContratosPorDocumento(fornecedor.id, normalized.cpf_cnpj)
  await vincularPrecosPorNome(fornecedor.id, normalized.razao_social)
  return { data: fornecedor, error: null }
}

export type FornecedorImportRow = FornecedorInput & { linha: number; erro?: string }

export function parseFornecedorRows(
  rows: Record<string, string>[],
): FornecedorImportRow[] {
  return rows.map((row, index) => {
    const input = normalizeFornecedorInput({
      razao_social: row.razao_social || row['Razão Social'] || row['Razao Social'] || '',
      nome_fantasia: row.nome_fantasia || row['Nome Fantasia'] || '',
      tipo_pessoa: (row.tipo_pessoa || row['Tipo de Pessoa'] || '') as TipoPessoa,
      cpf_cnpj: row.cpf_cnpj || row['CPF/CNPJ'] || row.cnpj || row.cpf || '',
      cep: row.cep || row.CEP || '',
      logradouro: row.logradouro || row.Logradouro || '',
      numero: row.numero || row['Número'] || row.Numero || '',
      complemento: row.complemento || row.Complemento || '',
      bairro: row.bairro || row.Bairro || '',
      cidade: row.cidade || row.Cidade || '',
      uf: row.uf || row.UF || '',
      responsavel_nome:
        row.responsavel_nome || row['Nome do Responsável'] || row.responsavel || '',
      telefone: row.telefone || row.Telefone || '',
      celular: row.celular || row.Celular || '',
      email: row.email || row['E-mail'] || row.Email || '',
    })
    const erro = validateFornecedorInput(input) || undefined
    return { ...input, linha: index + 2, erro }
  })
}

export async function importarFornecedores(
  rows: FornecedorImportRow[],
): Promise<{ ok: number; erros: string[] }> {
  const erros: string[] = []
  let ok = 0

  for (const row of rows) {
    if (row.erro) {
      erros.push(`Linha ${row.linha}: ${row.erro}`)
      continue
    }

    const { linha, erro: _e, ...input } = row
    let existingId: string | null = null

    if (input.cpf_cnpj) {
      const { data } = await supabase
        .from('fornecedores')
        .select('id')
        .eq('cpf_cnpj', input.cpf_cnpj)
        .maybeSingle()
      existingId = data?.id ?? null
    }

    const { error } = await salvarFornecedor(input, existingId || undefined)
    if (error) erros.push(`Linha ${linha}: ${error}`)
    else ok++
  }

  return { ok, erros }
}
